import { useMutation, useQueryClient } from '@tanstack/react-query';
import { unarchiveBoard, type UnarchiveBoardRequest } from '@/features/class/boardsApi';

/**
 * POST /midl/api/v3/un-archive-board/<boardId> — un-archives a class (board),
 * i.e. "Move to profile".
 *
 * Wired from `ClassFormModal` so the archived-classes "Move to profile" action
 * persists to the real backend. On success we refresh the server-driven lists:
 *   - `['boards','joined']`            → the class reappears in the active
 *     sidebar/profile list.
 *   - `['settings','archived-classes']` → the class drops off the Archived
 *     classes page.
 *
 * (Unlike archive, we don't hand-patch `rawUser` here: re-adding a board needs
 * its full login-payload shape, which we don't have client-side, so we let the
 * joined-boards query refetch supply it.)
 */
export function useUnarchiveBoardMutation() {
  const queryClient = useQueryClient();

  return useMutation<void, unknown, UnarchiveBoardRequest>({
    mutationKey: ['class', 'board', 'unarchive'],
    mutationFn: (vars) => unarchiveBoard(vars),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['boards', 'joined'] });
      void queryClient.invalidateQueries({ queryKey: ['settings', 'archived-classes'] });
    },
  });
}
